import { Flex, Grid, Stack, Switch, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { Customer, Declaration, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, HideElementFor, MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { DeclarationStakeholdersFormValues } from './validateDeclarationStakeholdersForm';

export type ExtendedDeclaration = Declaration & {
  customer?: Customer;
};

interface SupplyChainAndCustomsSectionsProps {
  form: UseFormReturnType<DeclarationStakeholdersFormValues>;
  editDisabled?: boolean;
  declaration?: ExtendedDeclaration;
  declarationGroup: string;
  isExport?: boolean;
}

export const SupplyChainAndCustomsSections: React.FC<SupplyChainAndCustomsSectionsProps> = ({
  form,
  editDisabled,
  declaration,
  declarationGroup,
  isExport,
}) => {
  const { data: settings } = useSettings();
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  const isHighValueBE = settings?.country === EnvironmentCountries.BE && isHighValueDeclaration(declarationGroup);
  const isNlExport = settings?.country === EnvironmentCountries.NL && !!isExport;

  return (
    <>
      <Stack mb="xl">
        <Flex justify="space-between" align="center">
          <Title order={5} fw={600}>
            Additional supply chain actor
          </Title>
          <Switch
            {...form.getInputProps('stakeholders.additionalSupplyChainActor.header')}
            checked={form.values.stakeholders.additionalSupplyChainActor.header}
            label="Header"
            disabled={editDisabled}
            size="lg"
          />
        </Flex>

        <Grid>
          <HideElementFor systems={['DMS']} declarationGroup={declarationGroup} condition={() => isHighValueBE}>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('stakeholders.additionalSupplyChainActor.name')} label="Name" disabled={editDisabled} />
            </Grid.Col>
          </HideElementFor>
          <Grid.Col span={6}>
            <EucdmCode path="stakeholders.additionalSupplyChainActor.identificationNumber">
              <TextInput
                {...form.getInputProps('stakeholders.additionalSupplyChainActor.identificationNumber')}
                label="Identification number"
                disabled={editDisabled}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <HideElementFor systems={['DMS']} declarationGroup={declarationGroup} condition={() => isHighValueBE}>
          <Grid>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('stakeholders.additionalSupplyChainActor.address.street')} label="Street and number" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('stakeholders.additionalSupplyChainActor.address.postcode')} label="Postcode" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('stakeholders.additionalSupplyChainActor.address.city')} label="City" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={3}>
              <MultiLangCodes
                {...form.getInputProps('stakeholders.additionalSupplyChainActor.address.country')}
                label="Country"
                code="CL199"
                disabled={editDisabled}
                system={codelistSystems}
              />
            </Grid.Col>
          </Grid>
        </HideElementFor>

        <Grid>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.additionalSupplyChainActor.role">
              <MultiLangCodes
                {...form.getInputProps('stakeholders.additionalSupplyChainActor.role')}
                label="Role"
                code="CL704"
                disabled={editDisabled}
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>

      {!settings?.isForwarder && (
        <HideElementFor condition={() => (declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL) || isExport}>
          <Stack mb="xl">
            <Title order={5} fw={600}>
              Person providing a guarantee
            </Title>

            <Grid>
              <Grid.Col span={6}>
                <TextInput label="Identification number" value={declaration.stakeholders?.personProvidingGuarantee?.identificationNumber} disabled />
              </Grid.Col>
            </Grid>
          </Stack>

          <Stack mb="xl">
            <Title order={5} fw={600}>
              Person paying the customs duty
            </Title>

            <Grid>
              <Grid.Col span={6}>
                <TextInput label="Identification number" value={declaration.stakeholders?.personPayingTheCustomDuty?.identificationNumber} disabled />
              </Grid.Col>
            </Grid>
          </Stack>
        </HideElementFor>
      )}
    </>
  );
};
